<template lang="pug">
    .play-box
        template(v-if='!isLoading')
            template(v-if="!prepareForGame")
                game-header(
                    @soundStateChanged ="headerKey = !headerKey"
                    :key="headerKey"
                    :time='getTime'
                    :slug="type"
                    :game-type="parameters.courseType"
                    :is-homework="!!exerciseId"
                )
                .play-box__game(v-if="!gameFinished && !prepareForGame && !isNextExampleButtonShown && !hideGameHeader")
                    .play-box__example
                        .reels-container(v-if="showReels && !gameFinished && !isNextExampleButtonShown")
                            reels(
                                :windowWidth="windowWidth"
                                :couples="couples"
                                @dropItemInRightReel="dropItemInRightReel"
                                :game-started="gameStarted"
                                :mode="parameters.mode"
                                :drag-started="dragStarted"
                                :type-of-right-column="typeOfRightColumn"
                                :disable-reply-button="disableReplyButton"
                                :rotate-button-clicked="rotateButtonClicked"
                                @endCarouselRotate="endCarouselRotate"
                                @showPlayButton="showPlayButton = true"
                                @addFocusOnReplyButton="addFocusOnReplyButton"
                                :block-reels-scroll="blockReelsScroll"
                                @blockReelsScroll="blockReelsScroll = isDragging = true"
                                @unlockReelsScroll="blockReelsScroll = isDragging = false"
                                @dropItemInAnswersBox="dropItemInAnswersBox"
                                :is-mobile="isMobile",
                                :are-values-viewable="areValuesViewable",
                                :are-answers-checked="areAnswersChecked"
                            )
                    .play-box__links(v-if='!gameFinished && !isNextExampleButtonShown')
                        a.play-box__link(href="javascript://", @click="rotateCarousel")
                            svg-icon.link__icon(name="spin", width="52px", height="52px")
                            | {{ $i18n.t('trainer.trainer_gamePlayBox_rotateCarousel') }}
                        .play-box__buttons.play-box__buttons--game(
                            v-if='!gameFinished && !isNextExampleButtonShown'
                        )
                            cs-button.play-box__button#reply-button(
                                v-if='gameStarted',
                                :disabled='disableReplyButton',
                                @click='processReply'
                            )
                                | {{ $i18n.t('trainer.trainer_gamePlayBox_reply') }}
                            cs-button.play-box__button#check-answers-button(
                                v-if='gameStarted',
                                :disabled='disableCheckAnswersButton',
                                @click='checkAnswers'
                            )
                                | {{ $i18n.t('trainer.trainer_gamePlayBox_showAnswers') }}
                    drop.play-box__drop(@drop='dropItemInAnswersBox', v-if='!gameFinished && showAnswersContainer && displayDropsBox')
                        answer-container(
                            v-show="displayAnswersContainer"
                            :answers='itemsForAnswersBox',
                            :game-started='gameStarted',
                            links-type='image',
                            @dragStarted='dragStarted = true',
                            @dragEnded='dragStarted = false'
                        )
                .play-box__end-container(v-if="(gameFinished || isNextExampleButtonShown) && !isOlympiad")
                    fireworks(
                        v-if='gameFinished && !isOlympiad && !prepareForGame',
                        :is-mobile='isMobile'
                    )
                    .play-box__reaction-container
                        result-image.play-box__image(status="success")
                    result-buttons(
                        :exercise-id="exerciseId"
                        :games-type="parameters.courseType"
                        show-restart-button
                        @restartGame="restartGame"
                    )
                        template(slot="mainButton")
                            cs-button(
                                v-if="isNextExampleButtonShown"
                                @click='getExample'
                                v-focus=''
                            )
                                | {{ $i18n.t('trainer.trainer_gamePlayBox_playOn') }}
                        slot
        cs-loader(v-else)
</template>

<script>
    import VocabularyWordsGameItemsApi from '../../../../api/games/vocabularyWordsGameItems';
    import Reels from './Reels.vue';
    import { Drag, Drop } from 'vue-drag-drop';
    import _ from 'lodash';
    import playBoxGameMixin from '../../../../utils/trainer/playBoxGameMixin.js';
    import olympiadAnswerTimerMixin from '../../../../utils/olympiads/olympiadAnswerTimerMixin.js';
    import AnswerContainer from './AnswerContainer.vue';

    const DEFAULT_VOLUME = 0.6;

    function DEFAULT_DATA() {
        return {
            showPlayButton: false,
            chainOfAttempts: [[]],
            typeOfRightColumn: '',
            showReels: true,
            dragStarted: false,
            allInputsAreFilled: false,
            rightColumnWasDroppedFirstTime: false,
            blockReelsScroll: false,
            prepareForGame: true,
            amountOfExamples: 0,
            isNextExampleButtonShown: false,
            hideGameHeader: false,
            isAttemptCorrect: false,
            itemsForAnswersBox: [],
            answersBoxCopy: [],
            couples: [],
            isDragging: false,
            rotateButtonClicked: false,
            displayDropsBox: true,
            areAnswersChecked: false,
            areValuesViewable: false,
        };
    }

    export default {
        mixins: [playBoxGameMixin, olympiadAnswerTimerMixin],
        data() {
            return DEFAULT_DATA();
        },
        watch: {
            couples: {
                handler(couples) {
                    if (this.typeOfRightColumn !== 'image') {
                        let allInputsAreFilled = _.find(couples, couple => {
                            return _.trim(couple.right.value).length === 0;
                        });
                        this.allInputsAreFilled = allInputsAreFilled === undefined;
                    }
                },
                deep: true,
                immediate: true,
            },
        },
        mounted() {
            this.answersBoxCopy = this.itemsForAnswersBox;
        },
        computed: {
            showAnswersContainer() {
                return this.typeOfRightColumn === 'image' && this.gameStarted;
            },
            allPairsNotSettled() {
                return this.typeOfRightColumn === 'image' ? this.itemsForAnswersBox.length !== 0 : false;
            },
            disableReplyButton() {
                return (this.allPairsNotSettled || !this.allInputsAreFilled) && this.displayDropsBox;
            },
            disableCheckAnswersButton() {
                let count = this.chainOfAttempts.length - 1;
                return this.chainOfAttempts[count].length < 3 || this.areAnswersChecked;
            },
            goodAnswers() {
                let count = 0;
                this.chainOfAttempts.forEach(example => {
                    example.forEach(attempt => (count += attempt.good));
                });
                return count;
            },
            badAnswers() {
                let count = 0;
                this.chainOfAttempts.forEach(example => {
                    example.forEach(attempt => (count += attempt.bad));
                });
                return count;
            },
        },
        methods: {
            fetchData() {
                this.getExercise()
                    .then(() => {
                        this.getExample();
                        this.$createjs.Sound.removeAllSounds();
                        this.$createjs.Sound.removeAllEventListeners();
                        this.$createjs.Sound.alternateExtensions = ['mp3', 'ogg'];
                        this.$createjs.Sound.registerSounds(this.soundObjects);
                        this.beforeStart();
                    })
                    .catch(() => {
                        this.isLoading = false;
                        this.$toasted.error(this.$root.$t('errors.getting_level'));
                        this.$router.push({ name: 'dashboard' });
                    });
            },
            beforeStart() {
                this.prepareForGame = false;
                if (!this.timer) {
                    this.setTimer();
                }
            },
            startGame() {
                this.gameStarted = true;
                if (this.answerDuration) {
                    this.startAnswerTimer();
                }
                this.dropRightColumn();
            },
            dropRightColumn() {
                this.showReels = false;
                this.$nextTick(() => {
                    if (this.typeOfRightColumn === 'image') {
                        this.couples.forEach(couple => {
                            this.itemsForAnswersBox.push(couple.right);
                        });
                        this.itemsForAnswersBox = _.shuffle(this.itemsForAnswersBox);
                        if (!this.rightColumnWasDroppedFirstTime) {
                            this.$nextTick(() => {
                                this.shakeAnswerBlocks();
                            });
                            this.rightColumnWasDroppedFirstTime = true;
                        }
                    }
                    let typeForEmptyCell = this.typeOfRightColumn === 'image' ? 'dropZone' : 'textInput';
                    this.couples = this.couples.map(couple => {
                        couple.right = {
                            type: typeForEmptyCell,
                            value: '',
                        };
                        return couple;
                    });
                    this.showReels = true;
                });
            },
            dropItemInRightReel(index) {
                this.itemsForAnswersBox.splice(index, 1);
                this.dragStarted = false;
                this.isDragging = false;
            },
            dropItemInAnswersBox(couple) {
                if (couple.from !== 'answers-box') {
                    let item = _.cloneDeep(couple.item);
                    if (!this.isMobile) {
                        let dragEvent = arguments[arguments.length - 1];
                        let toDropIndex = dragEvent.srcElement.dataset.index;
                        if (toDropIndex) {
                            this.itemsForAnswersBox.splice(+toDropIndex, 0, item);
                        } else {
                            if (dragEvent.srcElement.childElementCount) {
                                toDropIndex = dragEvent.srcElement.children[0].dataset.index;
                                toDropIndex && this.itemsForAnswersBox.splice(+toDropIndex, 0, item);
                            }
                            !toDropIndex && this.itemsForAnswersBox.push(item);
                        }
                    } else {
                        this.itemsForAnswersBox.push(item);
                    }
                    this.couples[couple.index].right = {
                        type: 'dropZone',
                    };
                }
            },
            checkAnswers() {
                if (this.parameters.rightColumn === 'image') {
                    this.couples.forEach(couple => {
                        couple.right.value = null;
                        couple.right = this.answersBoxCopy.find(item => item.id === couple.left.id);
                    });
                    this.displayDropsBox = false;
                    this.areAnswersChecked = true;
                    this.areValuesViewable = true;
                } else {
                    this.couples.forEach(couple => {
                        couple.right.value = couple.left.firstLetter || couple.left.lastLetter;
                    });
                    this.areValuesViewable = true;
                    this.areAnswersChecked = true;
                }
            },
            processReply() {
                this.stopAnswerTimer();
                let elemOfExampleAttempts = {
                    good: 0,
                    bad: 0,
                };
                for (let index = 0; index < this.couples.length; index++) {
                    if (this.isItCorrectCouple(this.couples[index].left, this.couples[index].right)) {
                        this.couples[index] = null;
                        elemOfExampleAttempts.good++;
                    } else {
                        elemOfExampleAttempts.bad++;
                    }
                }
                this.couples = this.couples.filter(element => element);
                this.chainOfAttempts[this.amountOfExamples].push(elemOfExampleAttempts);
                if (!this.couples.length) {
                    this.amountOfExamples++;
                    this.isAttemptCorrect = true;
                    this.hideGameHeader = true;
                    if (this.amountOfExamples === this.parameters.numOfExamples) {
                        this.couplesDone();
                        return;
                    }
                    if (!this.$offline && (this.user.type === 'student' || this.user.type === 'olympiads-student')) {
                        this.sendStat();
                    }
                    this.resetData();
                    if (this.isOlympiad) {
                        this.chainOfAttempts.push([]);
                        this.isNextExampleButtonShown = false;
                        this.getExample();
                    }
                    return;
                }
                if (this.isOlympiad) {
                    this.resetData();
                    this.amountOfExamples++;
                    this.chainOfAttempts.push([]);
                    this.isNextExampleButtonShown = false;
                    this.isAttemptCorrect = false;
                    if (this.amountOfExamples === this.parameters.numOfExamples) {
                        this.couplesDone();
                        return;
                    }
                    this.getExample();
                }
                this.dropRightColumn();
                if (!this.$offline && (this.user.type === 'student' || this.user.type === 'olympiads-student')) {
                    this.isAttemptCorrect = false;
                    this.sendStat();
                }
            },
            resetData() {
                this.isNextExampleButtonShown = true;
                this.gameStarted = false;
                this.hideGameHeader = false;
                this.showReels = false;
                this.rightColumnWasDroppedFirstTime = false;
                if (this.answerTimer) {
                    clearInterval(this.answerTimer);
                    this.answerTimer = null;
                }
                this.stopTimer();
                this.timer = null;
                this.couples = [];
                this.itemsForAnswersBox = [];
            },
            getExample() {
                this.areAnswersChecked = false;
                this.areValuesViewable = false;
                this.displayDropsBox = true;
                this.answersBoxCopy = this.itemsForAnswersBox;
                if (this.isAttemptCorrect && !this.isOlympiad) {
                    this.chainOfAttempts.push([]);
                    this.sendStat();
                }
                this.isLoading = true;
                let paramsForRequest = {
                    amountOfPairs: this.parameters.amountOfPairs,
                    themes: this.parameters.themes,
                    isHintEnabled: this.parameters.isHintEnabled,
                    chosenLetters: this.parameters.chosenLetters,
                };
                VocabularyWordsGameItemsApi.getVocabularyWords(paramsForRequest)
                    .then(couples => {
                        couples.forEach(item => {
                            let leftItem = {
                                type: this.parameters.leftColumn,
                                value:
                                    this.parameters.leftColumn === 'image'
                                        ? item.value
                                        : item[this.parameters.leftColumn],
                                id: item.id,
                            };
                            let rightItem = {
                                type: this.parameters.rightColumn,
                                value:
                                    this.parameters.rightColumn === 'image'
                                        ? item.value
                                        : item[this.parameters.rightColumn],
                                id: item.id,
                            };
                            if (this.parameters.rightColumn === 'image') {
                                rightItem[this.parameters.leftColumn] = item[this.parameters.leftColumn];
                            } else {
                                leftItem[this.parameters.rightColumn] = item[this.parameters.rightColumn];
                            }
                            this.couples.push({
                                left: leftItem,
                                right: rightItem,
                            });
                        });
                        this.typeOfRightColumn = this.parameters.rightColumn;
                        this.isLoading = false;
                        this.$nextTick(this.startGame);
                    })
                    .catch(err => this.showErrorMessage(err));
                if (!this.timer && !this.prepareForGame) {
                    this.setTimer();
                }
                this.isNextExampleButtonShown = false;
            },
            isItCorrectCouple(leftCell, rightCell) {
                if (this.typeOfRightColumn === 'image') {
                    return leftCell.value === rightCell[this.parameters.leftColumn];
                }
                return _.trim(leftCell[this.typeOfRightColumn]) === _.trim(rightCell.value.toLowerCase());
            },
            couplesDone() {
                if (!this.isOlympiad) {
                    this.soundInstance = this.$createjs.Sound.play('gameDone');
                    this.soundInstance.volume = DEFAULT_VOLUME;
                }
                this.gameFinished = true;
                this.sendStat();
            },
            refreshGameStatBeforeSend() {
                this.gameStat.chainOfAttempts = this.chainOfAttempts;
                this.gameStat.attempts = this.isAttemptCorrect
                    ? this.chainOfAttempts[this.amountOfExamples - 1].length
                    : this.chainOfAttempts[this.amountOfExamples].length;
                this.gameStat.answers = this.amountOfExamples;
                this.gameStat.percent = this.calculatePercent();
                this.gameStat.isCompleted = this.gameFinished;
                this.gameStat.executionTime = this.time;
                this.gameStat.isCorrect = true;
                this.gameStat.parameters = _.cloneDeep(this.parameters);
            },
            calculatePercent() {
                let total = this.goodAnswers + this.badAnswers;
                if (total > 0) {
                    return parseInt((this.goodAnswers / total) * 100);
                }
                return 0;
            },
            shakeAnswerBlocks() {
                let answerBlocks = document.getElementsByClassName('answer');
                let amount = answerBlocks.length;
                let timeouts = [];
                for (let i = 0; i < amount; i++) {
                    timeouts.push(
                        setTimeout(() => {
                            if (answerBlocks[i]) {
                                answerBlocks[i].classList.add('shake');
                            }
                        }, i * 100)
                    );
                }
                setTimeout(() => {
                    for (let j = 0; j < timeouts.length; j++) {
                        clearTimeout(timeouts[j]);
                    }
                    for (let i = 0; i < amount; i++) {
                        if (answerBlocks[i]) {
                            answerBlocks[i].classList.remove('shake');
                        }
                    }
                }, 2000);
            },
            handleScrollOfAnswersBox(e) {
                e = window.event || e;
                let delta = Math.max(-1, Math.min(1, e.wheelDelta || -e.detail));
                document.querySelector('.answers-container').scrollLeft -= delta * 65;
                e.preventDefault();
            },
            addFocusOnReplyButton() {
                let replyButton = document.getElementById('reply-button');
                replyButton.focus();
            },
            beforeGameRestart() {
                Object.assign(this, DEFAULT_DATA());
            },
            rotateCarousel() {
                this.rotateButtonClicked = true;
            },
            endCarouselRotate() {
                this.rotateButtonClicked = false;
            },
        },
        components: {
            AnswerContainer,
            Reels,
            Drag,
            Drop,
        },
    };
</script>

<style scoped lang="scss">
    @import '@/assets/sass/games/playbox.scss';

    .play-box--warning {
        display: table;
    }

    .play-box__game {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-direction: column;
        max-width: 100%;
        @include media($xlg) {
            flex-direction: row;
        }
    }

    .play-box__rounds {
        font-weight: 500;
        font-size: $font-size-16;
        line-height: $font-size-19;
        color: $hsl-color-green-58;
        margin-bottom: 20px;
    }

    .play-box__example {
        max-width: 900px;
        display: flex;
        margin-bottom: 20px;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        @include media($lg) {
            height: 100%;
        }
    }

    .play-box__example--full-height {
        height: 100%;
    }

    .reels-container {
        overflow: hidden;
        height: 100%;
        max-height: 500px;
    }

    .play-box__field {
        padding: 0 20px;
        text-align: center;
        font-size: 18px;
        user-select: none;
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
    }

    .play-box__drop {
        display: flex;
        justify-content: center;
        min-height: 110px;
        width: 100%;
        @include media($sm) {
            min-height: 150px;
        }
        @include media($md) {
            min-height: 195px;
        }
    }

    .play-box__word {
        padding: 10px;
        margin: 0 10px 10px 0;
        background: $x-color-white;
        border-radius: $border-radius-5;

        word-break: break-all;

        cursor: pointer;
    }

    .play-box__word:last-child {
        margin-right: 0;
    }

    .play-box__word--selected {
        color: $x-color-white;
        background: $hsl-color-green-58;
    }

    .play-box__word--unselected {
        color: $x-color-white;
        background: $hsl-color-red-62;
    }

    .play-box__header {
        display: flex;
        flex-direction: column;
        align-items: center;
        font-weight: 500;
        font-size: 22px;
        line-height: 26px;
        margin-bottom: 20px;
        @include media($sm) {
            margin-bottom: 30px;
        }
        @include media($md) {
            font-size: 32px;
            line-height: 38px;
        }
    }

    .play-box__title {
        margin-bottom: 20px;
    }

    .play-box__subtitle {
        margin-bottom: 0;
    }

    .play-box__end-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 100%;
    }

    .play-box__reaction-container {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-direction: column;
        width: 100%;
    }

    .play-box__time {
        font-weight: 500;
        font-size: 32px;
        line-height: 38px;
        text-align: center;
        color: $hsl-color-green-58;
        margin-bottom: 30px;
    }

    .play-box__answers {
        font-weight: 500;
        font-size: 18px;
        line-height: 21px;
        text-align: center;
        @include media($md) {
            font-size: 22px;
            line-height: 26px;
        }
        @include media($lg) {
            font-size: 32px;
            line-height: 38px;
        }
        @include media($xl) {
            font-size: 42px;
            line-height: 50px;
        }
    }

    .play-box__image {
        max-width: 100%;
        margin-bottom: 30px;
        @include media($md) {
            margin-bottom: 40px;
        }
    }

    .play-box__buttons {
        display: flex;
        flex-direction: column;
        margin-top: 20px;
        @include media($sm) {
            margin-top: 25px;
        }
    }

    .play-box__buttons--bottom {
        margin: 30px 0;
        flex-direction: row;
    }

    .play-box__buttons--game {
        flex-direction: column;
        @include media($xlg) {
            margin: 0 0 0 30px;
            flex-direction: row;
        }
        @include media($xl) {
            margin: 30px 0 10px;
            flex-direction: row;
        }
        @include media($lg) {
            flex-direction: row;
        }
        @include media($xxl) {
            margin: 0 0 0 30px;
            flex-direction: row;
        }
    }

    .play-box__buttons--bottom {
        margin: 30px 0 0;
        flex-direction: row;
    }

    .play-box__button {
        margin: 10px 0;
        @include media($lg) {
            margin: 0 20px;
        }
    }

    .play-box__button:first-child {
        margin-top: 0;
        @include media($lg) {
            margin-left: 0;
        }
    }

    .play-box__button:last-child {
        margin-bottom: 0;
        @include media($lg) {
            margin-right: 0;
        }
    }

    .play-box__button--bottom {
        margin: 0 20px;
    }

    .play-box__button--bottom:first-child {
        margin-left: 0;
    }

    .play-box__button--bottom:last-child {
        margin-right: 0;
    }

    .play-box__links {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        margin-bottom: 20px;
        @include media($sm) {
            margin-left: 20px;
            margin-bottom: 25px;
        }
    }

    .play-box__button--bottom {
        margin: 0 20px;
    }

    .play-box__link {
        display: flex;
        align-items: center;
        justify-content: center;
        color: $hsl-color-blue-56;
        text-decoration: underline;
        margin-bottom: 15px;
        &:hover {
            color: $hsl-color-blue-56;
            text-decoration: none;
        }
    }

    .link__icon {
        margin-right: 20px;
    }

    .play-box__game-info {
        align-self: flex-end;
        @include media($md) {
            align-self: auto;
        }
    }

    .warning {
        display: table-cell;
        vertical-align: middle;
        text-align: center;
    }
</style>
